/**
 * 成就系统
 * ------------------------------------------------------------------------
 * 把 ASSET_CHECKLIST.md 中定义的 20 个成就的“解锁条件”统一收口到这里：
 *   1. 本地去重 —— 同一个成就只上报一次（存储在 puzzleUtils.ts 的
 *      GameStats.unlockedAchievements 里，复用现有的 `blazegrids_stats`
 *      localStorage 存储，不另起一套存储）。
 *   2. 平台上报 —— 仅在 Capacitor.isNativePlatform() 为 true 时，调用
 *      capacitor-game-connect-7 上报到 iOS Game Center / Android Google
 *      Play Games Services；Web/PWA 环境下优雅跳过，不抛错。
 *   3. iOS 和 Android 的成就 ID 是两套完全独立的体系（见下方
 *      ACHIEVEMENT_IDS 的注释），需要人工在 App Store Connect /
 *      Google Play Console 创建成就后回填真实 ID。
 *
 * 上层调用方（DailyPage.tsx / ShareModal.tsx）只需要在游戏状态变化的
 * 准确时机调用 `unlockAchievement(key)`，或对于依赖累计统计的成就调用
 * `evaluateVictoryAchievements` / `evaluateShareAchievement`。
 */
import { Capacitor } from '@capacitor/core'
import { CapacitorGameConnect } from 'capacitor-game-connect-7'
import { loadGameStats, saveGameStats, type GameStats } from '../utils/puzzleUtils'

/**
 * 内部成就 key，与 ASSET_CHECKLIST.md 中的素材文件名 slug 一一对应
 * （store-assets/achievements/{game-center,google-play-games}/NN-<key>.png）
 */
export type AchievementKey =
  | 'first-spark'      // 01 首次填入一个正确字母
  | 'word-aflame'      // 02 首次完整填对一个横向或纵向单词
  | 'clue-seeker'      // 03 首次使用提示
  | 'grid-filled'      // 04 首次填满所有可输入格子
  | 'first-victory'    // 05 首次在倒计时结束前解完谜题
  | 'double-flame'     // 06 累计解完 2 个谜题
  | 'clean-burn'       // 07 首胜后再无提示解完 1 个谜题
  | 'quick-study'      // 08 首胜后再于 45 秒内解完 1 个谜题
  | 'firestarter'      // 09 累计解完 3 个谜题
  | 'share-the-heat'   // 10 解完 3 个谜题后成功分享一次结果
  | 'five-alarm'       // 11 累计解完 5 个谜题
  | 'speed-flame'      // 12 30 秒内解完 1 个谜题
  | 'pure-fire'        // 13 累计无提示解完 3 个谜题
  | 'from-the-ashes'   // 14 超时失败后，下一局成功完成（隐藏成就）
  | 'week-of-fire'     // 15 连续 7 个自然日完成每日谜题
  | 'grid-veteran'     // 16 累计解完 10 个谜题
  | 'flawless-ten'     // 17 累计无提示解完 10 个谜题
  | 'inferno-pace'     // 18 20 秒内无提示解完 1 个谜题
  | 'grid-guardian'    // 19 累计解完 25 个谜题
  | 'eternal-blaze'    // 20 连续 30 个自然日完成每日谜题

interface AchievementPlatformIds {
  ios: string
  android: string
}

/**
 * 内部成就 key -> 平台成就 ID 映射表。
 *
 * iOS（Apple Game Center / GameKit）：
 *   - Achievement ID 由你自己在 App Store Connect 里创建时手动填写，
 *     App Store Connect -> 你的 App -> 功能(Features) -> 游戏中心(Game Center)
 *     -> 成就(Achievements) -> 新建。
 *   - 这里约定使用反向域名风格：com.netherfire.blazegrids.achievement.<slug>
 *     （下划线版本，因为 App Store Connect 的 ID 里不允许连字符时可直接用这个）。
 *   - TODO(人工/运营)：在 App Store Connect 里用
 *     store-assets/achievements/game-center/NN-xxx.png 逐个创建 20 个成就，
 *     ID 直接照抄下面这一列即可（不需要改动代码），保存即可生效。
 *
 * Android（Google Play Games Services v2）：
 *   - Achievement ID 是 Google 在 Play Console 创建成就后自动生成的一串
 *     不透明字符串（形如 "CgkI17..."), 无法自己指定。
 *   - 2026-08-10：20 个成就已在 Google Play Console 创建完成（Draft 状态，
 *     总分 925/2000），下面的 android 值是 Play Console 生成的真实 ID，
 *     完整配置记录见 README.md「Google Play Games 后台状态」一节。
 *   - 发布前仍需：完成 OAuth 同意屏幕的用户数据政策确认、用 Play App Signing
 *     的 SHA-1 创建 Android OAuth 凭据、加入测试人员、发布 Play Games
 *     Services 项目——这几步做完之前，真机上报会因为未登录/项目未发布而
 *     静默失败（catch 住只打 warning，不影响游戏本身）。
 */
const ACHIEVEMENT_IDS: Record<AchievementKey, AchievementPlatformIds> = {
  'first-spark': { ios: 'com.netherfire.blazegrids.achievement.first_spark', android: 'CgkIvPmUp6ocEAIQDA' },
  'word-aflame': { ios: 'com.netherfire.blazegrids.achievement.word_aflame', android: 'CgkIvPmUp6ocEAIQBQ' },
  'clue-seeker': { ios: 'com.netherfire.blazegrids.achievement.clue_seeker', android: 'CgkIvPmUp6ocEAIQCw' },
  'grid-filled': { ios: 'com.netherfire.blazegrids.achievement.grid_filled', android: 'CgkIvPmUp6ocEAIQAQ' },
  'first-victory': { ios: 'com.netherfire.blazegrids.achievement.first_victory', android: 'CgkIvPmUp6ocEAIQEw' },
  'double-flame': { ios: 'com.netherfire.blazegrids.achievement.double_flame', android: 'CgkIvPmUp6ocEAIQEg' },
  'clean-burn': { ios: 'com.netherfire.blazegrids.achievement.clean_burn', android: 'CgkIvPmUp6ocEAIQAg' },
  'quick-study': { ios: 'com.netherfire.blazegrids.achievement.quick_study', android: 'CgkIvPmUp6ocEAIQBA' },
  'firestarter': { ios: 'com.netherfire.blazegrids.achievement.firestarter', android: 'CgkIvPmUp6ocEAIQEA' },
  'share-the-heat': { ios: 'com.netherfire.blazegrids.achievement.share_the_heat', android: 'CgkIvPmUp6ocEAIQAA' },
  'five-alarm': { ios: 'com.netherfire.blazegrids.achievement.five_alarm', android: 'CgkIvPmUp6ocEAIQBw' },
  'speed-flame': { ios: 'com.netherfire.blazegrids.achievement.speed_flame', android: 'CgkIvPmUp6ocEAIQCg' },
  'pure-fire': { ios: 'com.netherfire.blazegrids.achievement.pure_fire', android: 'CgkIvPmUp6ocEAIQCA' },
  'from-the-ashes': { ios: 'com.netherfire.blazegrids.achievement.from_the_ashes', android: 'CgkIvPmUp6ocEAIQDg' },
  'week-of-fire': { ios: 'com.netherfire.blazegrids.achievement.week_of_fire', android: 'CgkIvPmUp6ocEAIQBg' },
  'grid-veteran': { ios: 'com.netherfire.blazegrids.achievement.grid_veteran', android: 'CgkIvPmUp6ocEAIQAw' },
  'flawless-ten': { ios: 'com.netherfire.blazegrids.achievement.flawless_ten', android: 'CgkIvPmUp6ocEAIQEQ' },
  'inferno-pace': { ios: 'com.netherfire.blazegrids.achievement.inferno_pace', android: 'CgkIvPmUp6ocEAIQDQ' },
  'grid-guardian': { ios: 'com.netherfire.blazegrids.achievement.grid_guardian', android: 'CgkIvPmUp6ocEAIQCQ' },
  'eternal-blaze': { ios: 'com.netherfire.blazegrids.achievement.eternal_blaze', android: 'CgkIvPmUp6ocEAIQDw' }
}

/** 是否已经尝试过静默登录 Game Center / Play Games（进程内只做一次） */
let signInAttempted = false

/**
 * 应用启动后调用一次，尝试静默登录 Game Center（iOS）/ Play Games（Android）。
 * 未登录不影响本地成就记录，只是无法上报到平台；因此这里只是 best-effort，
 * 失败时静默吞掉错误。仅在原生平台上执行。
 */
export function initGameServices(): void {
  if (signInAttempted) return
  signInAttempted = true

  if (!Capacitor.isNativePlatform()) return
  const platform = Capacitor.getPlatform()
  if (platform !== 'ios' && platform !== 'android') return

  CapacitorGameConnect.signIn().catch((error) => {
    // 用户未登录 Game Center / Play Games、取消登录、或插件不可用，都属于正常场景
    console.warn('[achievements] Game Center / Play Games sign-in skipped:', error)
  })
}

/**
 * 把成就上报给当前原生平台（Game Center / Play Games）。
 * - Web/PWA 环境直接跳过。
 * - 平台成就 ID 还是占位符（尚未在后台创建）时也跳过，不发起无意义的原生调用。
 * - 任何原生调用失败都只打印警告，不向上抛出，绝不影响游戏本身。
 */
async function reportToNativePlatform(key: AchievementKey): Promise<void> {
  if (!Capacitor.isNativePlatform()) return

  const platform = Capacitor.getPlatform()
  if (platform !== 'ios' && platform !== 'android') return

  const ids = ACHIEVEMENT_IDS[key]
  const achievementID = platform === 'ios' ? ids.ios : ids.android

  if (!achievementID || achievementID.startsWith('TODO_')) {
    // 尚未在 App Store Connect / Google Play Console 配置真实 ID，跳过上报
    return
  }

  try {
    await CapacitorGameConnect.unlockAchievement({ achievementID })
  } catch (error) {
    console.warn(`[achievements] failed to report "${key}" to ${platform}:`, error)
  }
}

/**
 * 解锁一个成就（幂等）。
 * 可以在“条件当前是否成立”的每一次检查里直接调用它——内部会做本地去重，
 * 保证同一个成就只会真正解锁 / 上报一次，调用方不需要自己维护“是否首次”的状态。
 */
export function unlockAchievement(key: AchievementKey): void {
  const stats = loadGameStats()
  if (stats.unlockedAchievements.includes(key)) return

  const updated: GameStats = {
    ...stats,
    unlockedAchievements: [...stats.unlockedAchievements, key]
  }
  saveGameStats(updated)

  void reportToNativePlatform(key)
}

/**
 * 判断某个成就当前在本地是否已解锁（供 UI 展示用，例如“我的成就”页面，如果以后需要的话）
 */
export function isAchievementUnlocked(key: AchievementKey): boolean {
  return loadGameStats().unlockedAchievements.includes(key)
}

/**
 * 胜利结算时（在倒计时结束前解完谜题）需要的上下文。
 * `stats` 必须是调用 `recordGameResult({ won: true, ... })` 之后的最新统计数据。
 */
export interface VictoryAchievementContext {
  /** 本局用时（毫秒） */
  durationMs: number
  /** 本局使用的提示次数 */
  hintsUsed: number
  /** recordGameResult 返回的、已经计入本局之后的统计数据 */
  stats: GameStats
  /** 本局之前玩家是否已经至少胜利过一次 */
  hasPriorVictory: boolean
  /** 上一局是否以超时失败结束、而本局是胜利 */
  cameBackFromTimeout: boolean
}

/**
 * 根据本局胜利的上下文，检查并解锁所有满足条件的“胜利类”成就
 * （对应 ASSET_CHECKLIST.md 中 #5、#6、#7、#8、#9、#11、#12、#13、#14、#15、#16、#17、#18、#19、#20）
 */
export function evaluateVictoryAchievements(ctx: VictoryAchievementContext): void {
  const { durationMs, hintsUsed, stats, hasPriorVictory, cameBackFromTimeout } = ctx

  unlockAchievement('first-victory') // #5 首次在倒计时结束前解完谜题

  if (stats.gamesWon >= 2) unlockAchievement('double-flame') // #6 累计解完 2 个
  if (hasPriorVictory && hintsUsed === 0) unlockAchievement('clean-burn') // #7 首胜后再无提示解完 1 个
  if (hasPriorVictory && durationMs <= 45000) unlockAchievement('quick-study') // #8 首胜后再 45 秒内解完 1 个
  if (stats.gamesWon >= 3) unlockAchievement('firestarter') // #9 累计解完 3 个
  if (stats.gamesWon >= 5) unlockAchievement('five-alarm') // #11 累计解完 5 个
  if (durationMs <= 30000) unlockAchievement('speed-flame') // #12 30 秒内解完
  if (stats.noHintWins >= 3) unlockAchievement('pure-fire') // #13 累计无提示解完 3 个
  if (cameBackFromTimeout) unlockAchievement('from-the-ashes') // #14 超时失败后下一局成功完成（隐藏）
  if (stats.currentStreak >= 7) unlockAchievement('week-of-fire') // #15 连续 7 个自然日
  if (stats.gamesWon >= 10) unlockAchievement('grid-veteran') // #16 累计解完 10 个
  if (stats.noHintWins >= 10) unlockAchievement('flawless-ten') // #17 累计无提示解完 10 个
  if (durationMs <= 20000 && hintsUsed === 0) unlockAchievement('inferno-pace') // #18 20 秒内无提示解完
  if (stats.gamesWon >= 25) unlockAchievement('grid-guardian') // #19 累计解完 25 个
  if (stats.currentStreak >= 30) unlockAchievement('eternal-blaze') // #20 连续 30 个自然日
}

/**
 * 分享成功后调用：检查“解完 3 个谜题后成功分享一次结果”（#10）是否满足
 */
export function evaluateShareAchievement(): void {
  const stats = loadGameStats()
  if (stats.gamesWon >= 3) unlockAchievement('share-the-heat')
}
